import { useNavigate } from 'react-router-dom'
import { useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAnalysisStore } from '@/store/analysisStore'
import { useAuthStore } from '@/store/authStore'
import { haptic } from '@/lib/haptics'

// Animated sparkle component
const Sparkle = ({
  className,
  size = 24,
  color = '#FF8C33',
  delay = 0
}: {
  className?: string
  size?: number
  color?: string
  delay?: number
}) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    animate={{
      scale: [1, 1.3, 1],
      opacity: [0.7, 1, 0.7],
      rotate: [0, 15, 0]
    }}
    transition={{
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay
    }}
  >
    <path d="M12 0L13.5 9L22 12L13.5 15L12 24L10.5 15L2 12L10.5 9L12 0Z" />
  </motion.svg>
)

const products = [
  { brand: 'Round Lab', product: 'Dokdo Toner', image: '/image/product-toner.png', large: false },
  { brand: 'Abib Heartleaf', product: 'Essence Pad', image: '/image/product-pad.png', large: false },
  { brand: 'AESTURA', product: 'Atobarrier 365', image: '/image/product-atobarrier.png', large: false },
  { brand: 'BIODANCE', product: 'Deep Mask Sheet', image: '/image/product-mask.png', large: false },
  { brand: 'Dr.G', product: 'Soothing Cream', image: '/image/product-cream.png', large: false },
  { brand: 'Round Lab', product: 'Sunscreen 50ml', image: '/image/product-sunscreen.png', large: false },
  { brand: 'Abib', product: 'Collagen Patch', image: '/image/Abib_Collagen Eye Patch 1carousel-slide.png', large: true },
  { brand: 'Anua', product: 'Heartleaf Cleanse', image: '/image/Anua_Heartfleaf_porecontrol_cleansing_oil 1carousel-slide.png', large: true },
  { brand: 'Round Lab', product: 'TXA4 Serum', image: '/image/Anua_Niacinamide10_TXA4_Serum 1carousel-slide.png', large: true },
  { brand: 'BanilaCo', product: 'CleanitZero', image: '/image/BanilaCo_Cleanitzero 1carousel-slide.png', large: true },
  { brand: 'Beplain', product: 'Milk Peeling Gel', image: '/image/beplain_MungBean_Milkpeelinggel 1carousel-slide.png', large: true },
  { brand: 'Biodance', product: 'Real Deep Mask', image: '/image/Biodance_BioCollagen_RealDeepMask 1carousel-slide.png', large: true },
  { brand: 'BOH', product: '3D Lifting Cream', image: '/image/BOH_Probioderm_3DLiftingCream 1carousel-slide.png', large: true },
  { brand: 'D Alba', product: 'First Spray Serum', image: '/image/d_Alba_FristSpraySerum 1carousel-slide.png', large: true },
  { brand: 'D Alba', product: 'UV Essence', image: '/image/d_Alba_UVEssence 1carousel-slide.png', large: true },
  { brand: 'Krave Beauty', product: '24Carrot Retinol', image: '/image/KraveBeauty_24Carrot_Retinol 1carousel-slide.png', large: true },
  { brand: 'Laneige', product: 'SleepingMask', image: '/image/Laneige_BouncyandFirm_SleepingMask 1carousel-slide.png', large: true },
  { brand: 'Madagascar', product: 'CentellaCream', image: '/image/Madagascar_Centella_ProbioCica_Bakuchiol_EveCream 1carousel-slide.png', large: true },
  { brand: 'Mixsoon', product: 'Bean Essence', image: '/image/Mixoon_BeanEssence 1carousel-slide.png', large: true },
  { brand: 'Torriden', product: 'Soothing Cream', image: '/image/Torriden_Divein_SoothingCream 1carousel-slide.png', large: true },
]

export function WelcomePage() {
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const { analysisResults } = useAnalysisStore()
  const { hasPaid, demoMode } = useAuthStore()

  // Auto-redirect users based on their progress
  const hasRedirected = useRef(false)
  useEffect(() => {
    if (analysisResults && !hasRedirected.current) {
      hasRedirected.current = true
      if (hasPaid || demoMode) {
        navigate('/report', { state: { showWelcomeToast: true } })
      } else {
        navigate('/teaser', { state: { showWelcomeToast: true } })
      }
    }
  }, [analysisResults, hasPaid, demoMode, navigate])

  useEffect(() => {
    const video = videoRef.current
    if (video) {
      // Essential for iOS autoplay
      video.muted = true
      video.setAttribute('muted', '')
      video.setAttribute('playsinline', '')
      video.playbackRate = 0.8
      video.load()

      const attemptPlay = () => {
        const playPromise = video.play()
        if (playPromise !== undefined) {
          playPromise.catch(() => {})
        }
      }

      video.addEventListener('loadedmetadata', attemptPlay)
      video.addEventListener('canplaythrough', attemptPlay)

      // Also try after a short delay
      setTimeout(attemptPlay, 100)
      setTimeout(attemptPlay, 500)

      return () => {
        video.removeEventListener('loadedmetadata', attemptPlay)
        video.removeEventListener('canplaythrough', attemptPlay)
      }
    }
  }, [])

  const handleStart = () => {
    haptic('light')
    // Smart redirect based on user progress
    if (analysisResults) {
      if (hasPaid || demoMode) {
        navigate('/report', { state: { showWelcomeToast: true } })
      } else {
        navigate('/teaser', { state: { showWelcomeToast: true } })
      }
    } else {
      navigate('/home')
    }
  }

  return (
    <div className="relative h-dvh h-screen w-full overflow-hidden bg-white">

      {/* Logo */}
      <motion.h1
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="absolute top-[20px] left-[19px] z-20 text-[25px]"
        style={{ fontFamily: "'Oleo Script', cursive" }}
      >
        <span className="text-[#688192]">K-Beauty</span>
        <span className="text-[#46C0C6]"> AI</span>
      </motion.h1>

      {/* Background video */}
      <video
        ref={videoRef}
        src="/image/backgroundvideo2.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="absolute -top-[100px] left-0 right-0 h-[759px] w-full object-cover pointer-events-none"
        style={{ objectFit: 'cover' }}
      />


      {/* Bottom frosted glass panel */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[240px] z-10"
        style={{
          // Solid fallback for browsers without backdrop-filter support (older Android)
          background: 'linear-gradient(to bottom, rgba(180, 160, 150, 0.85) 0%, rgba(0, 0, 0, 0.4) 100%)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
        }}
      />

      {/* Gradient overlay at very bottom */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[215px] z-[5]"
        style={{
          background: 'linear-gradient(123deg, rgba(165, 133, 122, 1) 15%, rgba(223, 205, 191, 1) 90%)',
        }}
      />

      {/* Content on frosted panel */}
      <div className="absolute bottom-0 left-0 right-0 h-[320px] z-20 flex flex-col">
        {/* Product carousel - infinite auto scroll */}
        <div className="h-[88px] overflow-hidden mb-[10px]">
          <motion.div
            className="flex gap-[21px] h-full items-center"
            animate={{ x: [0, -1920] }}
            transition={{
              x: {
                duration: 50,
                repeat: Infinity,
                ease: "linear",
              },
            }}
          >
            {/* Double the products for seamless loop */}
            {[...products, ...products].map((product, index) => (
              <div
                key={`${product.brand}-${product.product}-${index}`}
                className="flex-shrink-0 w-[75px] h-[88px] rounded-[5px] bg-white shadow-sm flex flex-col items-center py-1 relative"
                style={{
                  borderRadius: '5px',
                  border: '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <div className="flex-1 flex items-center justify-center p-1 overflow-hidden">
                  <img
                    src={product.image}
                    alt={`${product.brand} ${product.product}`}
                    className={`object-contain ${product.large ? 'max-h-[60px] max-w-[65px]' : 'max-h-[55px] max-w-[45px]'}`}
                  />
                </div>
                <div className="text-[6px] text-[#656565] text-left px-1 leading-tight w-full">
                  <p className="truncate font-medium">{product.brand}</p>
                  <p className="truncate font-normal">{product.product}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Text section with sparkles */}
        <div className="relative px-[17px]">
          {/* Animated sparkles around text */}
          <Sparkle className="absolute -top-2 -left-1" size={18} color="#FF8C33" delay={0} />
          <Sparkle className="absolute top-8 -right-2" size={14} color="#46C0C6" delay={0.4} />
          <Sparkle className="absolute bottom-4 right-4" size={16} color="#F596FA" delay={0.8} />
          <Sparkle className="absolute bottom-0 left-12" size={12} color="#7EC8E3" delay={1.2} />

          {/* Headline - responsive text size, each phrase on one line */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="leading-[1.15] text-white mt-[5px] font-semibold whitespace-nowrap"
            style={{
              fontFamily: "'Anybody', sans-serif",
              fontSize: 'clamp(22px, 7vw, 32px)'
            }}
          >
            Discover Your Perfect
            <br />
            <span className="text-[#46C0C6]">K-Beauty</span> Routine
          </motion.h2>

          {/* Subtitle - responsive text size, single line */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-white/90 mt-[10px] leading-[1.4]"
            style={{
              fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(12px, 3.5vw, 15px)'
            }}
          >
            Personalized Recommendations from 1,000+ Products powered by AI Skin Analysis.
          </motion.p>
        </div>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleStart}
          className="self-end mt-auto mb-[20px] mr-[17px] w-[224px] h-[60px] bg-[#46C0C6] rounded-[33px] text-white text-[25px] font-bold"
          style={{ fontFamily: "'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif" }}
        >
          Start Now
        </motion.button>
      </div>
    </div>
  )
}
